<template>
    <div class="pager-bar-container">
        <div v-if="selectedRows || $slots.fab" class="floating-action-bar-container">
            <div class="floating-action-bar">
                <p v-if="selectedRows">
                    <strong>{{ selectedRows }}</strong> {{ selectedRowsItemName }}
                    selected
                </p>
                <span v-if="selectedRows && $slots.fab">&middot;</span>
                <slot name="fab" />
                <b-button v-if="selectedRows" size="sm" variant="link" class="p-1" @click="$emit('clear-selection')">
                    <i class="fa fa-close" />
                </b-button>
            </div>
        </div>

        <div :class="{ 'pager-bar glass-pane': true, 'no-stick': noStick, 'single': hideLeft && hideRight }">
            <div v-if="!hideLeft" class="d-flex align-items-center">
                <slot name="left" />
                <b-form v-if="showPageSizeSelector" class="page-size">
                    <div>
                        <label class="mr-2 mb-0" for="pager-bar-page-size">Page Size</label>
                    </div>
                    <b-select id="pager-bar-page-size" :value="perPage" size="sm" :options="pageSizeOptions"
                        :disabled="loading" @change="$emit('change-page-size', $event)" />
                </b-form>
                <div v-if="loading">
                    <b-spinner label="Spinning" class="mr-2" small />
                    <strong>Loading...</strong>
                </div>
                <slot name="left-after" />
            </div>

            <div class="flex-fill">
                <slot name="middle" />
                <b-pagination v-if="!hidePager" :value="value" :total-rows="filteredRows !== undefined ? filteredRows : totalRows"
                    :per-page="perPage" :disabled="loading" align="center" @input="$emit('input', $event)" />
            </div>

            <div v-if="!hideRight" class="d-flex align-items-center flex-row-reverse">
                <slot name="right" />
                <div v-if="showTotal" class="text-right">
                    <template v-if="isFiltered">
                        {{ filteredRows?.toLocaleString() }} matched {{ filteredRowsItemName }}
                    </template>
                    <div>
                        <component :is="isFiltered ? 'small' : 'div'" :class="{ 'text-muted': isFiltered }">
                            {{ totalRows?.toLocaleString() }} total {{ totalRowsItemName }}
                        </component>
                    </div>
                </div>
                <slot name="right-after" />
            </div>
        </div>
    </div>

</template>

<script>

export default {
    name: "BottomPagerBar",
    props: {
        totalRows: Number,
        filteredRows: Number,
        perPage: Number,
        value: Number,
        showTotal: Boolean,
        showPageSizeSelector: Boolean,
        hidePager: Boolean,
        hideLeft: Boolean,
        hideRight: Boolean,
        noStick: Boolean,
        loading: Boolean,
        selectedRows: Number,
        itemName: {
            type: String,
            default: "item"
        },
        scrollTarget: String  // CSS selector for element to scroll into view on page change
    },
    data() {
        return {
            startingPerPage: this.perPage
        };
    },
    methods: {
        itemNameFormat(number) {
            let name = this.itemName;
            if (number === 1) return name;
            if (name.endsWith("y")) name = name.substring(0, name.length - 1) + "ie";
            return name + "s";
        }
    },
    computed: {
        pageSizeOptions() {
            const defaults = [25, 50, 100, 250, 500, 1000];

            if (this.startingPerPage && !defaults.includes(this.startingPerPage)) {
                defaults.push(this.startingPerPage);
            }

            defaults.sort((a, b) => a - b);

            return defaults.map(n => ({ value: n, text: n.toLocaleString() }));
        },
        isFiltered() {
            return this.filteredRows !== undefined && this.filteredRows !== this.totalRows;
        },
        selectedRowsItemName() {
            return this.itemNameFormat(this.selectedRows);
        },
        filteredRowsItemName() {
            return this.itemNameFormat(this.filteredRows);
        },
        totalRowsItemName() {
            return this.itemNameFormat(this.totalRows);
        },
    },
    watch: {
        value(newVal, oldVal) {
            if (this.scrollTarget && newVal !== oldVal) {
                this.$nextTick(() => {
                    const element = document.querySelector(this.scrollTarget);
                    if (element) {
                        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                });
            }
        }
    }
}

</script>

<style scoped lang="scss">
.pager-bar-container {
    position: sticky;
    bottom: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    margin: 0 -15px -10px -15px;
}

.floating-action-bar-container {
    text-align: center;
}

.floating-action-bar {
    background: rgba(0, 0, 0, 0.75);
    -webkit-backdrop-filter: blur(15px);
    backdrop-filter: blur(15px);
    padding: 8px 15px;
    border-radius: 10px;
    margin: 0 10px 10px 10px;
    color: white;
    display: inline-flex;
    gap: 6px;
    align-items: center;

    >* {
        margin: 0;
    }

    .btn-link {
        color: white;
    }
}

.pager-bar {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    align-items: center;
    min-height: 50px;
    border-top: 1px solid rgba(0, 0, 0, .05);
    box-shadow: inset 0px 7px 6px -6px rgba(0, 0, 0, .1);
    padding: 10px 15px 10px 15px;

    .b-pagination {
        margin-bottom: 0;
    }

    &.no-stick {
        position: relative;
    }

    .d-flex {
        gap: 12px;
    }
}

.pager-bar.single
{
    display: flex;
}

.page-size {
    font-size: .9em;

    select {
        font-size: 1.1em;
        //font-weight: 600;
        padding-top: 1px;
        padding-bottom: 1px;
        height: 25px;
    }
}
</style>
